<script setup lang="ts">
/**
 * CaButton — SATU-SATUNYA tombol yang boleh dipakai halaman prototipe.
 * Membungkus <UButton> Nuxt UI (perilaku + aksesibilitas dari reka-ui),
 * lalu memaksakan aturan design system kita: tap target minimum 44px,
 * CTA uang 52px, radius 10px, dan feedback tekan scale(0.97).
 *
 * Temuan audit #4: tombol lama efektif 34–43px. Di sini tidak mungkin lagi.
 * Kalau library diganti kelak, yang berubah hanya file ini — bukan 13 halaman.
 */
type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'link' | 'emas'
type Size = 'sm' | 'md' | 'lg'

const props = withDefaults(
  defineProps<{
    variant?: Variant
    size?: Size
    block?: boolean
    loading?: boolean
    disabled?: boolean
    icon?: string
    trailingIcon?: string
    to?: string
    href?: string
    type?: 'button' | 'submit' | 'reset'
    label?: string
  }>(),
  { variant: 'primary', size: 'md', type: 'button' }
)

const petaVarian: Record<Variant, { color: any; variant: any }> = {
  primary: { color: 'primary', variant: 'solid' },
  secondary: { color: 'neutral', variant: 'outline' },
  ghost: { color: 'neutral', variant: 'ghost' },
  danger: { color: 'error', variant: 'solid' },
  link: { color: 'primary', variant: 'link' },
  // `emas` — CTA uang tema v3: latar gold cerah #FFBE3D + tinta #0A0E1C (11,6:1).
  // Warna sebenarnya datang dari kelas `.ca-btn-emas` (assets/css/v3.css); di sini
  // kita hanya menumpang perilaku "solid" milik UButton.
  emas: { color: 'primary', variant: 'solid' }
}

/** Tinggi kontrol menurut design system (bukan menurut default library). */
const petaTinggi: Record<Size, string> = {
  sm: 'min-h-9 px-3.5',  // 36px — aksi sekunder padat saja
  md: 'min-h-11 px-5',   // 44px — minimum untuk jempol
  lg: 'min-h-13 px-6'    // 52px — CTA yang menghasilkan uang
}
const petaUkuranU: Record<Size, any> = { sm: 'sm', md: 'md', lg: 'lg' }

const kelasBase = computed(() =>
  [
    'ca-focus rounded-control font-semibold',
    'transition-transform duration-[120ms] ease-[cubic-bezier(0.2,0,0,1)] active:scale-[0.97]',
    // Varian `link` tetap 44px tinggi (tap target), tapi rata kiri dan selebar teksnya
    // supaya daftar tautan footer tidak jadi tombol raksasa yang melebar.
    props.variant === 'link'
      ? 'min-h-11 px-0 justify-start w-fit'
      : `justify-center ${petaTinggi[props.size]}`,
    props.variant === 'emas' ? 'ca-btn-emas' : ''
  ].join(' ')
)
</script>

<template>
  <UButton
    :to="to"
    :href="href"
    :type="type"
    :disabled="disabled"
    :loading="loading"
    :icon="icon"
    :trailing-icon="trailingIcon"
    :label="label"
    :block="block"
    :size="petaUkuranU[size]"
    :color="petaVarian[variant].color"
    :variant="petaVarian[variant].variant"
    :ui="{ base: kelasBase }"
  >
    <slot />
  </UButton>
</template>
